import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import {
  LayoutDashboard, Users, MapPin, Gift, CreditCard,
  ChevronRight, Eye, Loader2, Camera,
  Cake, PartyPopper, TrendingUp, Clock,
  ClipboardList, Stamp, Star, BarChart3, Store,
} from "lucide-react";
import { toast } from "sonner";
import TruckSwitcher from "@/components/vendor/TruckSwitcher";
import VendorJoyrideTour, { useVendorTour } from "@/components/vendor/VendorJoyrideTour";
import TipOfTheDay from "@/components/shared/TipOfTheDay";
import MilestoneCelebration from "@/components/vendor/MilestoneCelebration";

export default function VendorDashboard() {
  const navigate = useNavigate();
  const { runTour, completeTour, replayTour } = useVendorTour();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBirthdayGate, setShowBirthdayGate] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeOrderCount, setActiveOrderCount] = useState(0);
  const [nearbyCustomers, setNearbyCustomers] = useState([]);
  const [grantingId, setGrantingId] = useState(null);
  const [bestTime, setBestTime] = useState(null);
  const [consistency, setConsistency] = useState(null);
  const [birthdaySeason, setBirthdaySeason] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);

  const logoFileRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/dashboard`);
      setData(res.data);
      if (res.data?.birthday_program_participant === undefined || res.data?.birthday_program_participant === null) {
        setShowBirthdayGate(true);
      }
      // Fetch logo
      try {
        const photos = await axios.get(`${API}/vendor/photos`);
        if (photos.data?.logo_photo?.url) setLogoUrl(photos.data.logo_photo.url);
      } catch {}
      // Fetch active order count
      try {
        const ordersRes = await axios.get(`${API}/orders/incoming`);
        setActiveOrderCount(ordersRes.data.total_active || 0);
      } catch {}
      // Fetch nearby customers for punch granting
      if (res.data?.business_id) {
        try {
          const custRes = await axios.get(`${API}/punchcard/nearby-customers/${res.data.business_id}`);
          setNearbyCustomers(custRes.data.customers || []);
        } catch {}
      }
      // Fetch best time, consistency, birthday season in parallel
      Promise.all([
        axios.get(`${API}/vendor/best-time-today`).catch(() => ({ data: {} })),
        axios.get(`${API}/vendor/consistency`).catch(() => ({ data: {} })),
        axios.get(`${API}/vendor/birthday-season`).catch(() => ({ data: {} })),
        axios.get(`${API}/food-holidays/upcoming?days=14`).catch(() => ({ data: { holidays: [] } })),
      ]).then(([btRes, conRes, bsRes, fhRes]) => {
        setBestTime(btRes.data);
        setConsistency(conRes.data);
        setBirthdaySeason(bsRes.data);
        setUpcomingHolidays(fhRes.data?.holidays || []);
      });
    } catch { /* handled */ }
    finally { setLoading(false); }
  }, []);

  const handleBirthdayEnroll = async (participating) => {
    setEnrolling(true);
    try {
      const res = await axios.post(`${API}/vendor/confirm-birthday-program`, { participating });
      if (participating) {
        toast.success("Welcome to the Birthday Program!");
        setShowBirthdayGate(false);
      } else {
        // Show the encouraging message — don't close the gate
        toast.error(res.data.message);
      }
    } catch { toast.error("Something went wrong"); }
    finally { setEnrolling(false); }
  };

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Fetch review stats for widget
  useEffect(() => {
    axios.get(`${API}/vendor/review-stats`).then(res => {
      if (res.data?.has_reviews) setReviewStats(res.data);
    }).catch(() => {});
  }, []);

  const grantPunch = async (customerId, customerName) => {
    setGrantingId(customerId);
    try {
      const res = await axios.post(`${API}/punchcard/vendor-grant`, { customer_id: customerId });
      toast.success(`Punch granted to ${customerName}! (${res.data.punches}/${res.data.target})${res.data.double_punch_bonus ? " — 2x bonus!" : ""}`);
      if (data?.business_id) {
        const custRes = await axios.get(`${API}/punchcard/nearby-customers/${data.business_id}`);
        setNearbyCustomers(custRes.data.customers || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to grant punch");
    }
    setGrantingId(null);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target.result.split(",")[1];
        await axios.post(`${API}/vendor/photo/upload`, {
          photo_type: "logo_photo",
          photo_data: base64,
        });
        setLogoUrl(e.target.result);
        toast.success("Profile photo uploaded");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="vendor-dashboard">
        <p className="text-white/60 text-sm">Could not load dashboard</p>
      </div>
    );
  }

  const pct = data.profile_completeness;
  const pctColor = pct >= 80 ? "bg-[#22C55E]" : pct >= 50 ? "bg-amber-400" : "bg-red-500";
  const pctTextColor = pct >= 80 ? "text-[#22C55E]" : pct >= 50 ? "text-amber-400" : "text-red-500";

  return (
    <div className="min-h-screen px-4 pt-14 pb-24" data-testid="vendor-dashboard">
      <VendorJoyrideTour run={runTour} onComplete={completeTour} />
      <TipOfTheDay pageKey="vendor_dashboard" />
      <MilestoneCelebration />
      {/* ── Birthday Program Onboarding Gate (Part 22L) ── */}
      {showBirthdayGate && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="birthday-program-gate">
          <div className="bg-[#1C1C1E] rounded-2xl p-6 max-w-sm w-full border border-amber-500/20 shadow-xl">
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-full bg-[#F59E0B] flex items-center justify-center mx-auto mb-3">
                <Cake className="w-7 h-7 text-[#1C1C1E]" />
              </div>
              <h2 className="text-lg font-bold text-white mb-1">Join the Birthday Program</h2>
              <p className="text-xs text-white/50 leading-relaxed">
                BirthdayDeaLight connects birthday celebrants with local trucks and restaurants. When you participate, birthday groups find you — and they rarely come alone.
              </p>
            </div>

            <div className="bg-[#2C2C2E] rounded-xl p-3 mb-4 space-y-2">
              <div className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-[#22C55E] mt-0.5 shrink-0" />
                <p className="text-[11px] text-white/60">A free birthday offer costs you one item — the group pays full price</p>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-[#22C55E] mt-0.5 shrink-0" />
                <p className="text-[11px] text-white/60">Get discovered by celebrants who would never have found you otherwise</p>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-[#22C55E] mt-0.5 shrink-0" />
                <p className="text-[11px] text-white/60">Build loyalty — celebrants come back as regulars with their friends</p>
              </div>
            </div>

            <button
              onClick={() => handleBirthdayEnroll(true)}
              disabled={enrolling}
              className="w-full py-3 rounded-xl bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-50 mb-2"
              data-testid="birthday-enroll-yes"
            >
              {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />}
              Yes, I want to participate!
            </button>
            <button
              onClick={() => handleBirthdayEnroll(false)}
              disabled={enrolling}
              className="w-full py-2 rounded-xl bg-transparent text-white/30 text-xs font-medium"
              data-testid="birthday-enroll-no"
            >
              Not right now
            </button>
          </div>
        </div>
      )}
      {/* Header with Profile Photo */}
      <div className="flex items-center gap-3 mb-5">
        <div
          className="relative w-12 h-12 rounded-xl overflow-hidden bg-[#2C2C2E] flex items-center justify-center cursor-pointer group"
          onClick={() => logoFileRef.current?.click()}
          data-testid="truck-logo-upload"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Truck Logo" className="w-full h-full object-cover" />
          ) : (
            <LayoutDashboard className="w-5 h-5 text-[#F59E0B]" />
          )}
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
            {uploadingLogo ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Camera className="w-4 h-4 text-white" />
            )}
          </div>
          <input
            ref={logoFileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleLogoUpload(e.target.files[0])}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-white truncate" data-testid="dashboard-truck-name">{data.truck_name}</h1>
            <TruckSwitcher activeBizId={data.business_id} onSwitch={() => { setLoading(true); fetchDashboard(); }} />
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-white/40">My Truck Dashboard</p>
            <button
              onClick={() => navigate("/vendor/portfolio")}
              className="text-[10px] text-[#F59E0B]/50 hover:text-[#F59E0B] font-medium flex items-center gap-0.5 transition"
              data-testid="portfolio-link"
            >
              <TrendingUp className="w-3 h-3" /> Portfolio
            </button>
            <button
              onClick={() => navigate("/vendor/achievements")}
              className="text-[10px] text-[#F59E0B]/50 hover:text-[#F59E0B] font-medium flex items-center gap-0.5 transition"
              data-testid="achievements-link"
            >
              Achievements
            </button>
          </div>
        </div>
      </div>

      {/* Profile Completeness — hidden at 100% */}
      {pct < 100 && (
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="profile-completeness">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-white/80">Profile Completeness</p>
          <span className={`text-sm font-bold ${pctTextColor}`} data-testid="completeness-pct">{pct}%</span>
        </div>
        <div className="w-full h-2 bg-[#1C1C1E] rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${pctColor}`} style={{ width: `${pct}%` }} />
        </div>
        {data.missing_fields?.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {data.missing_fields.slice(0, 5).map(f => {
              const route = (f.field === "offer_title" || f.field === "offer_summary") ? "/vendor/birthday-card"
                : f.field === "menu_items" ? "/vendor/menu"
                : (f.field === "truck_photo" || f.field === "menu_photo") ? "/vendor/photos"
                : f.field === "social_media" ? "/vendor/social"
                : "/vendor/truck-info";
              return (
                <button
                  key={f.field}
                  onClick={() => navigate(route)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1C1C1E] border border-white/5 hover:border-[#F59E0B]/30 transition"
                  data-testid={`missing-field-${f.field}`}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  <span className="text-[11px] text-white/60 flex-1 text-left">{f.label}</span>
                  <ChevronRight className="w-3 h-3 text-white/20" />
                </button>
              );
            })}
          </div>
        )}
      </section>
      )}

      {/* Reviews Widget */}
      {reviewStats && reviewStats.has_reviews && (
        <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3" data-testid="reviews-widget">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-white/80">Reviews</p>
            <button onClick={() => navigate("/vendor/reviews")} className="text-[10px] text-[#F59E0B] font-medium" data-testid="reviews-view-all">View All</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center">
              <div className="flex items-center justify-center gap-0.5 mb-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`w-2.5 h-2.5 ${s <= Math.round(reviewStats.avg_rating) ? "text-[#F59E0B] fill-[#F59E0B]" : "text-white/15"}`} />
                ))}
              </div>
              <p className="text-sm font-bold text-white">{reviewStats.avg_rating}</p>
              <p className="text-[9px] text-white/30">Avg Rating</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-white">{reviewStats.total}</p>
              <p className="text-[9px] text-white/30">Total Reviews</p>
            </div>
            <div className="text-center">
              {reviewStats.unread_negative > 0 ? (
                <p className="text-sm font-bold text-red-400">{reviewStats.unread_negative}</p>
              ) : (
                <p className="text-sm font-bold text-green-400">0</p>
              )}
              <p className="text-[9px] text-white/30">Needs Attention</p>
            </div>
          </div>
        </section>
      )}

      {/* ORDERS HUB — Big, unmissable */}
      <button
        onClick={() => navigate("/vendor/orders")}
        className="w-full mb-3 relative overflow-hidden rounded-2xl border-2 transition-all active:scale-[0.98]"
        style={{
          borderColor: activeOrderCount > 0 ? "rgba(56,189,248,0.4)" : "rgba(255,255,255,0.05)",
          background: activeOrderCount > 0
            ? "linear-gradient(135deg, rgba(56,189,248,0.15) 0%, rgba(14,165,233,0.08) 100%)"
            : "linear-gradient(135deg, rgba(44,44,46,1) 0%, rgba(28,28,30,1) 100%)",
        }}
        data-testid="vendor-orders-btn"
      >
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeOrderCount > 0 ? "bg-sky-500" : "bg-white/5"
              }`}>
                <ClipboardList className={`w-6 h-6 ${activeOrderCount > 0 ? "text-white" : "text-white/40"}`} />
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-base">Orders</p>
                <p className="text-white/40 text-xs">
                  {activeOrderCount > 0 ? "Tap to view incoming orders" : "No active orders right now"}
                </p>
              </div>
            </div>
            {activeOrderCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="bg-red-500 text-white text-sm font-black min-w-[28px] h-7 rounded-full flex items-center justify-center px-2 animate-pulse" data-testid="order-count-badge">
                  {activeOrderCount}
                </span>
                <ChevronRight className="w-5 h-5 text-white/30" />
              </div>
            )}
            {activeOrderCount === 0 && (
              <ChevronRight className="w-5 h-5 text-white/20" />
            )}
          </div>
          {activeOrderCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-sky-500/20 rounded-full overflow-hidden">
                <div className="h-full bg-sky-400 rounded-full animate-pulse" style={{ width: "60%" }} />
              </div>
              <span className="text-sky-400 text-[10px] font-bold">{activeOrderCount} active</span>
            </div>
          )}
        </div>
      </button>

      {/* Punch Card & QR */}
      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="punch-qr-row">
        <StatCard icon={<CreditCard className="w-4 h-4 text-[#F59E0B]" />} label="Punch Card" value={data.stats.punch_card_active ? "Active" : "Inactive"} active={data.stats.punch_card_active} testId="stat-punch" onClick={() => navigate("/vendor/punch-card")} />
        <StatCard icon={<Eye className="w-4 h-4 text-[#F59E0B]" />} label="QR Code" value="View" testId="stat-qr" onClick={() => navigate("/vendor/qr")} />
      </div>

      {/* Customers — Grant Punches */}

      {/* Holiday Campaigns Section Card */}
      {upcomingHolidays.length > 0 && (
        <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5" data-testid="holiday-campaigns-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">{upcomingHolidays[0].emoji}</span>
              <div>
                <p className="text-sm font-bold text-white">{upcomingHolidays[0].name}</p>
                <p className="text-[10px] text-white/40">
                  {upcomingHolidays[0].days_until === 0 ? "Today!" : upcomingHolidays[0].days_until === 1 ? "Tomorrow" : `${upcomingHolidays[0].days_until} days away`}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/vendor/holiday-campaigns")}
              className="px-3 py-1.5 rounded-lg bg-[#F59E0B] text-[#1C1C1E] text-[10px] font-bold active:scale-[0.97] transition"
              data-testid="setup-campaign-btn"
            >
              Set Up Campaign
            </button>
          </div>
          {upcomingHolidays.length > 1 && (
            <p className="text-[9px] text-white/30">+{upcomingHolidays.length - 1} more upcoming holidays</p>
          )}
        </section>
      )}
      {nearbyCustomers.length > 0 && (
        <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5" data-testid="dashboard-customers-section">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-bold text-white/80">Customers</p>
            <span className="text-[10px] text-white/30">{nearbyCustomers.length} recent</span>
          </div>
          <div className="space-y-2">
            {nearbyCustomers.slice(0, 5).map((c) => (
              <div key={c.customer_id} className="flex items-center justify-between bg-[#1C1C1E] rounded-lg px-3 py-2.5" data-testid={`dash-customer-${c.customer_id}`}>
                <div>
                  <p className="text-white text-xs font-medium">{c.name}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="flex gap-0.5">
                      {Array.from({ length: c.target }).map((_, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full ${i < c.punches ? "bg-[#F59E0B]" : "bg-white/10"}`} />
                      ))}
                    </div>
                    <span className="text-[9px] text-white/30 ml-1">{c.punches}/{c.target}</span>
                  </div>
                </div>
                <button
                  onClick={() => grantPunch(c.customer_id, c.name)}
                  disabled={grantingId === c.customer_id || c.punches >= c.target}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1 transition ${
                    c.punches >= c.target
                      ? "bg-green-500/20 text-green-400"
                      : "bg-[#F59E0B] text-black hover:bg-[#E08E0A]"
                  } disabled:opacity-50`}
                  data-testid={`dash-grant-${c.customer_id}`}
                >
                  {grantingId === c.customer_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : c.punches >= c.target ? (
                    "Full"
                  ) : (
                    <><Stamp className="w-3 h-3" /> Punch</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="quick-stats">
        <StatCard icon={<Users className="w-4 h-4 text-[#F59E0B]" />} label="Followers" value={data.stats.followers} testId="stat-followers" />
        <StatCard icon={<MapPin className="w-4 h-4 text-[#F59E0B]" />} label="Spotted" value={data.stats.spots_this_month} sub="this month" testId="stat-spots" />
        <StatCard icon={<Gift className="w-4 h-4 text-[#F59E0B]" />} label="Birthday Offer" value={data.stats.birthday_offer_active ? "Active" : "Inactive"} active={data.stats.birthday_offer_active} testId="stat-birthday" onClick={() => navigate("/vendor/birthday-special")} />
      </div>

      {/* Best Time Today (Task 7) */}
      {bestTime?.recommended_start_hour != null && (
        <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5" data-testid="best-time-card">
          <p className="text-[10px] text-[#F59E0B] font-bold uppercase tracking-wider mb-1">Best Time Today</p>
          <p className="text-xl font-black text-white">
            {bestTime.recommended_start_hour > 12 ? `${bestTime.recommended_start_hour - 12}PM` : bestTime.recommended_start_hour === 12 ? "12PM" : `${bestTime.recommended_start_hour}AM`}
            {" — "}
            {bestTime.recommended_end_hour > 12 ? `${bestTime.recommended_end_hour - 12}PM` : bestTime.recommended_end_hour === 12 ? "12PM" : `${bestTime.recommended_end_hour}AM`}
          </p>
          {bestTime.reasons?.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {bestTime.reasons.map((r, i) => (
                <p key={i} className="text-[10px] text-white/30 flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[#F59E0B] shrink-0" /> {r}
                </p>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Consistency Score (Task 8) */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5" data-testid="consistency-card">
        {consistency?.consistency_score != null ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Consistency Score</p>
              <p className={`text-2xl font-black ${
                consistency.consistency_score >= 0.8 ? "text-green-400" :
                consistency.consistency_score >= 0.6 ? "text-amber-400" : "text-red-400"
              }`}>{Math.round(consistency.consistency_score * 100)}%</p>
            </div>
            {consistency.consistency_streak > 0 && (
              <div className="text-right">
                <p className="text-lg font-black text-[#F59E0B]">{consistency.consistency_streak}</p>
                <p className="text-[9px] text-white/30">day streak</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-[10px] text-white/40 uppercase tracking-wider">Consistency Score</p>
            <p className="text-[11px] text-white/20 mt-1 italic">Start scheduling and going live to build your consistency score</p>
          </div>
        )}
      </section>

      {/* Birthday Season Calendar (Task 10) */}
      {birthdaySeason?.months && (
        <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5" data-testid="birthday-season-card">
          <p className="text-[10px] text-[#F59E0B] font-bold uppercase tracking-wider mb-1">Birthday Season Forecast</p>
          <p className="text-[9px] text-white/20 mb-3">When birthday celebrants in your area are most active</p>
          {(() => {
            const currentMonth = new Date().getMonth() + 1;
            const maxCount = Math.max(...birthdaySeason.months.map(m => m.birthday_user_count), 1);
            const top3 = [...birthdaySeason.months].sort((a, b) => b.birthday_user_count - a.birthday_user_count).slice(0, 3).map(m => m.month_number);
            return (
              <div className="flex items-end gap-1" data-testid="birthday-chart">
                {birthdaySeason.months.map(m => {
                  const pct = Math.max((m.birthday_user_count / maxCount) * 100, 4);
                  const isCurrent = m.month_number === currentMonth;
                  const isTop = top3.includes(m.month_number);
                  return (
                    <div key={m.month_number} className="flex-1 flex flex-col items-center gap-0.5">
                      <span className="text-[8px] text-white/30">{m.birthday_user_count || ""}</span>
                      <div
                        className={`w-full rounded-t-sm transition-all ${
                          isCurrent ? "bg-[#F59E0B]" : isTop ? "bg-[#F59E0B]/50" : "bg-white/10"
                        }`}
                        style={{ height: `${pct}px`, minHeight: "4px", maxHeight: "60px" }}
                      />
                      <span className={`text-[7px] ${isCurrent ? "text-[#F59E0B] font-bold" : "text-white/20"}`}>
                        {m.month_name.slice(0, 3)}
                      </span>
                      {/* Holiday markers */}
                      {m.holidays?.length > 0 && (
                        <div className="flex flex-col items-center gap-px mt-0.5" data-testid={`holiday-markers-${m.month_number}`}>
                          {m.holidays.slice(0, 2).map((h, hi) => (
                            <span key={hi} className={`w-1.5 h-1.5 rounded-full ${h.scope === "federal" ? "bg-amber-400" : "bg-sky-400"}`} title={h.name} />
                          ))}
                        </div>
                      )}
                      {/* Major event markers from PredictHQ */}
                      {m.major_events?.length > 0 && (
                        <div className="flex flex-col items-center gap-px mt-0.5" data-testid={`event-markers-${m.month_number}`}>
                          {m.major_events.slice(0, 2).map((ev, ei) => (
                            <span
                              key={ei}
                              className="w-1.5 h-1.5 rounded-full bg-orange-500"
                              title={`${ev.event_name} — Est. ${(ev.predicted_attendance || 0).toLocaleString()} attending`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <p className="text-[8px] text-white/15 mt-2 text-center">Data improves as BirthdayDeaLight grows in your area</p>
        </section>
      )}


      {/* Foot Traffic Insights — Coming Soon placeholder */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5 relative" data-testid="foot-traffic-card">
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/25 text-[9px] font-bold text-amber-400 uppercase tracking-wider" data-testid="foot-traffic-badge">Coming Soon</span>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0 mt-0.5">
            <BarChart3 className="w-4 h-4 text-sky-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-white/80">Foot Traffic Insights</p>
            <p className="text-[10px] text-white/30 leading-relaxed mt-1">Coming in a future update — see when your location is busiest by hour and day to maximize your go-live timing.</p>
          </div>
        </div>
      </section>

      {/* Square POS Insights — Coming Soon placeholder */}
      <section className="bg-[#2C2C2E] rounded-xl p-4 mb-3 border border-white/5 relative" data-testid="square-pos-card">
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/25 text-[9px] font-bold text-amber-400 uppercase tracking-wider" data-testid="square-pos-badge">Coming Soon</span>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
            <Store className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-white/80">Square POS Insights</p>
            <p className="text-[10px] text-white/30 leading-relaxed mt-1">Connect your Square account to see when customers visit, how often they return, and how your BirthdayDeaLight punch card performs. No revenue data ever accessed.</p>
          </div>
        </div>
      </section>

      {/* Profile Preview */}
      <button
        onClick={() => navigate("/vendor/preview")}
        className="w-full py-3.5 rounded-xl bg-[#F59E0B] text-[#1C1C1E] text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#F59E0B]/90 transition"
        data-testid="profile-preview-btn"
      >
        <Eye className="w-4 h-4" /> Profile Preview
      </button>
    </div>
  );
}

function StatCard({ icon, label, value, sub, active, testId, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`bg-[#2C2C2E] rounded-xl p-3.5 border transition text-left ${
        onClick ? "hover:border-[#F59E0B]/30 active:scale-[0.97] cursor-pointer" : ""
      } ${active === false && onClick ? "border-red-500/20" : "border-white/5"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <span className="text-[10px] text-white/40 uppercase tracking-wide">{label}</span>
        {onClick && <ChevronRight className="w-3 h-3 text-white/20 ml-auto" />}
      </div>
      <p className={`text-lg font-bold ${active === false ? "text-red-400" : active === true ? "text-[#22C55E]" : "text-white"}`}>
        {value}
      </p>
      {sub && <p className="text-[9px] text-white/30">{sub}</p>}
    </Tag>
  );
}
